// HollywoodSmileData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const hollywoodSmile: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Hollywood Smile Dubai",
            "description": "Luxury cosmetic dentistry in Dubai offering flawless smile transformations with dental veneers, teeth whitening, and full smile makeovers using cutting-edge technology.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Hollywood Smile Dubai"],
        "hero": {
            "title": "Hollywood Smile Dental Clinic in Dubai",
            "subtitle": "Premier Destination for Luxury Cosmetic Dentistry",
            "description": "Achieving a perfect smile boosts confidence and enhances your overall appearance. Our experienced dentists provide dental veneers, teeth whitening, and full smile makeovers, ensuring natural-looking, long-lasting results."
        },
        "howItWorks": {
            "title": "Get Your Hollywood Smile in 4 Easy Steps!",
            "description": "Achieving your perfect Hollywood smile is simpler than you think. Follow these four easy steps to transform your smile effortlessly."
        },
        "procedureSteps": {
            "heading": "What is the Hollywood Smile MakeOver?",
            "para": "A 'Hollywood Smile' is an all-encompassing dental transformation designed to give you a perfect, celebrity-white smile. This often includes several cosmetic procedures customized to the individual.",
            "steps": [
                {
                    "title": "Classic Hollywood Smile",
                    "desc": "Natural bright white with proper alignment and lip relationship.",
                    "icon": "fa-regular fa-smile"
                },
                {
                    "title": "Celebrity Hollywood Smile",
                    "desc": "Ultra-glam zone that gives off A-list vibes with veneers, whitening and orthodontic work.",
                    "icon": "fa-solid fa-star"
                },
                {
                    "title": "Veneer Hollywood Smile",
                    "desc": "Enhanced by porcelain veneers or Lumineers to adjust imperfections like discoloration or chips.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Whitening Hollywood Smile",
                    "desc": "Achieved through teeth whitening procedures including in-office or laser whitening.",
                    "icon": "fa-solid fa-broom"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of the Hollywood Smile in Dubai",
            "subheading": "Why Choose the Hollywood Smile?",
            "para": "Hollywood Smile Makeover in Dubai has several advantages, which is why it is one of the most popular dental treatments.",
            "items": [
                {
                    "title": "Improved Appearance",
                    "desc": "Get white, bright and symmetrical smile.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Covers Imperfections",
                    "desc": "Hides gaps, chips, and stains within a snap.",
                    "icon": "fa-solid fa-eye-slash"
                },
                {
                    "title": "Long Lasting Results",
                    "desc": "With proper care, quality veneers can last 10-15 years.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Fast Results",
                    "desc": "Captivating Smile Makeover in no time at all.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Increases Self-Esteem",
                    "desc": "Have greater confidence in social and work situations.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "Ideal Candidates for a Hollywood Smile",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "Those with stained, cracked, or crooked teeth",
                    "desc": "",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Those with diastema or irregularly sized teeth",
                    "desc": "",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Patients in good oral health",
                    "desc": "No severe gum disease",
                    "icon": "fa-solid fa-teeth-open"
                }
            ]
        },
        "preparationTips": {
            "heading": "Hollywood Smile MakeOver Preparation",
            "subheading": "",
            "para": "Today this modern approach of putting emphasis on visualization of teeth as an important unit of the face has replaced the old conventional principles of treating teeth.",
            "items": [
                {
                    "title": "Initial Consultation",
                    "desc": "Comprehensive examination and smile design planning.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Digital Smile Design",
                    "desc": "Using advanced software to visualize your new smile.",
                    "icon": "fa-solid fa-computer"
                },
                {
                    "title": "Dental Preparation",
                    "desc": "Any necessary cleaning or preliminary treatments.",
                    "icon": "fa-solid fa-teeth"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Providing a Five Star Hollywood Smile in Dubai, UAE, with expert care and performance.",
            "items": [
                {
                    "title": "Natural-Looking Smile",
                    "desc": "Flawless, symmetrical teeth that complement your facial features.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "Long-Lasting",
                    "desc": "High-quality materials ensure durability of your new smile.",
                    "icon": "fa-solid fa-hourglass-end"
                },
                {
                    "title": "Boosted Confidence",
                    "desc": "Transform your appearance and self-esteem.",
                    "icon": "fa-solid fa-thumbs-up"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Tips",
            "subheading": "",
            "para": "Proper care extends the life of your Hollywood Smile.",
            "items": [
                {
                    "title": "Regular Oral Hygiene",
                    "desc": "Brush and floss daily to maintain your new smile.",
                    "icon": "fa-solid fa-toothbrush"
                },
                {
                    "title": "Avoid Hard Foods",
                    "desc": "Protect your veneers from damage.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Routine Check-ups",
                    "desc": "Visit your dentist regularly for maintenance.",
                    "icon": "fa-solid fa-calendar-days"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does the Hollywood Smile procedure take?",
                    "answer": "Typically, the process takes 2-3 visits over a few weeks."
                },
                {
                    "question": "How long does Hollywood smile last?",
                    "answer": "A Hollywood Smile can last 10-15 years, depending on materials and oral care. Regular check-ups, good hygiene, and avoiding hard foods help extend its lifespan."
                },
                {
                    "question": "Are the veneers permanent?",
                    "answer": "Yes, veneers are designed to last over a decade with proper care."
                },
                {
                    "question": "Does the treatment hurt?",
                    "answer": "The procedure is minimally invasive and generally pain-free, with local anesthesia used when needed."
                },
                {
                    "question": "Is it a one-size-fits-all solution?",
                    "answer": "No, each Hollywood Smile is customized to your facial features and preferences."
                }
            ]
        },
        "treatmentAreas1": [
        ],
        "treatmentAreas2": [
        ]
    },
    ar: {
        "url": "",
        "meta": {
            "title": "",
            "description": "",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", ""],
        "hero": {
            "title": "عيادة هوليوود سمايل لطب الأسنان",
            "subtitle": "أحدث خدمات تجميل الأسنان",
            "description": "خبراء موثوق بهم من المشاهير\nرعاية فاخرة ومثالية دون ألم"
        },
        "howItWorks": {
            "title": "احصل على ابتسامة هوليوود في 4 خطوات سهلة!",
            "description": "تحقيق ابتسامتك المثالية أصبح أسهل مما تتخيل! اتبع الأربع خطوات التالية لتحصل على ابتسامة هوليوود دون أي مجهود:"
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "حجز استشارة مجانية",
                    "desc": "تواصل معنا عبر الهاتف أو قم بملء نموذج الاستفسار لحجز موعدك المجاني.",
                    "icon": "fa-regular fa-calendar-check"
                },
                {
                    "title": "خطة علاج مخصصة",
                    "desc": "سيقوم فريقنا بتصميم خطة علاجية تناسب أهدافك للحصول على الابتسامة المثالية.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "تحسين الابتسامة",
                    "desc": "يُنفذ العلاج المتفق عليه، مثل تبييض الأسنان، أو الفينير، أو نحت اللثة بأعلى درجات الدقة والاحترافية.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "اللمسات النهائية والنتائج",
                    "desc": "استمتع بابتسامة هوليوود المثالية لمدة طويلة!",
                    "icon": "fa-solid fa-face-smile-beam"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد ابتسامة هوليوود في دبي",
            "subheading": "لماذا تختار ابتسامة هوليوود؟",
            "para": "يُعد الحصول على ابتسامة هوليوود في دبي خيارًا مميزًا بفضل الفوائد العديدة التي يقدمها، مما يجعله علاجًا مطلوبًا من قبل السكان المحليين والزوار الدوليين على حد سواء.",
            "items": [
                {
                    "title": "زيادة الجمال",
                    "desc": "احصل على ابتسامة مشرقة وناصعة البياض ومتناسقة تمنحك مظهرًا جذابًا.",
                    "icon": "fa-solid fa-star"
                },
                {
                    "title": "تصحيح عيوب الأسنان",
                    "desc": "يعالج الفجوات بين الأسنان، والتشققات، والتصبغات بسهولة للحصول على مظهر مثالي.",
                    "icon": "fa-solid fa-teeth-open"
                },
                {
                    "title": "نتائج طويلة الأمد",
                    "desc": "تدوم الفينير عالية الجودة من 10 إلى 15 عامًا عند العناية بها بشكل جيد.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "تحول سريع",
                    "desc": "احصل على ابتسامة متألقة خلال زيارات معدودة فقط، دون الحاجة إلى إجراءات طويلة الأمد.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "زيادة الثقة بالنفس",
                    "desc": "استمتع بمظهر أكثر ثقة في المناسبات الاجتماعية والمهنية بفضل ابتسامتك المثالية.",
                    "icon": "fa-solid fa-face-grin-wide"
                }
            ]
        },
        "eligibility": {
            "heading": "المرشحون المثاليون لابتسامة هوليوود",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "الأفراد الذين يعانون من تغير لون الأسنان",
                    "desc": "أو تشققها، أو عدم استقامتها.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "الأشخاص الذين لديهم فراغات بين الأسنان",
                    "desc": "أو اختلافات في حجم الأسنان.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "المرضى الذين يتمتعون بصحة فموية جيدة",
                    "desc": "ولا يعانون من أمراض لثة خطيرة.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "كان أطباء الأسنان يعالجون كل سن على حدة ومشاكل التناسق بشكل منفصل قبل ظهور النهج المنظم لمفهوم ابتسامة هوليوود وبرامج تصميم الابتسامة الرقمية، حيث كان التركيز منصبًا على تصحيح المشاكل السنية مثل الكسور أو التسوس.",
            "items": []
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "",
            "items": [
                {
                    "title": "",
                    "desc": "",
                    "icon": ""
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم من الوقت يستغرق إجراء ابتسامة هوليوود؟",
                    "answer": "عادةً ما يستغرق الإجراء من زيارتين إلى ثلاث زيارات على مدار بضعة أسابيع."
                },
                {
                    "question": "كم تدوم ابتسامة هوليوود؟",
                    "answer": "يمكن أن تدوم ابتسامة هوليوود من 10 إلى 15 عامًا، وذلك حسب المواد المستخدمة والعناية بالفم. يساعد المتابعة الدورية مع طبيب الأسنان في إطالة عمرها بجانب الحفاظ على نظافة الفم، وتجنب الأطعمة الصلبة."
                },
                {
                    "question": "هل الفينير دائم؟",
                    "answer": "نعم، صُمم الفينير ليدوم لأكثر من عقد عند العناية به بشكل صحيح."
                },
                {
                    "question": "هل الإجراء مؤلم؟",
                    "answer": "الإجراء بسيط وعادةً ما يكون غير مؤلم، مع استخدام التخدير الموضعي عند الحاجة لضمان الراحة."
                },
                {
                    "question": "هل يمكنني اختيار درجة لون الفينير؟",
                    "answer": "بالتأكيد! سيعمل طبيب الأسنان معك لاختيار درجة اللون التي تتناسب مع ملامح وجهك وتفضيلاتك الشخصية."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/12/1-6.jpg"
            , beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Holiwood-Smile-in-Dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Holiwood-Smile-in-Dubai-3.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Holiwood-Smile-in-Dubai-3.jpg",
            ], introductionImage: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/12/2-6-1024x671.jpg"
        },
        product: {
            id: 1,
            title: "Hollywood Smile",
            price: 1000,
            description: "Hollywood Smile is a cosmetic dental procedure that enhances the appearance of your smile, giving you a bright and confident look."
        }
    }
}
